import "../../css/GlobalStyles.css";
import { memo, useCallback, useMemo, useState } from "react";
import TopHeader from "../../components/TopHeader";
import { Colors } from "../../utils/colors";
import {
  AccessLevel,
  AccountData,
  CustomTicketV2,
  Event,
  EventType,
  Follower,
  FollowerMembershipState,
  SavedFormQuestion,
  ThemeType,
} from "@markit/common.types";
import FullPostContent from "../../components/CreatorDashboard/Products/FullProduct/FullPostContent";
import Footer from "../../components/Footer";
import SlidingToggleButton from "../../components/Buttons/SlidingToggleButton";
import { DarkTheme, useTheme } from "../../hooks/useTheme";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { useDispatch, useSelector } from "react-redux";
import { onProfilePageNavigatePath } from "../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import InactiveProductState from "../../components/CreatorDashboard/Products/InactiveProductState";
import {
  accountActions,
  getAccountState,
  LoginState,
} from "../../redux/slices/accountSlice";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { getUserFollowerData } from "../../utils/FirebaseUtils";
import { checkIfFollowerIsSubscribed } from "../../utils/userUtils";
import LoadingScreen from "../LoadingScreen";
import ProfilePic from "../../components/ProfilePic";
import {
  getPossessiveName,
  getVisibleCustomTickets,
} from "@markit/common.utils";
import { isDesktop } from "react-device-detect";
import FullProductContent from "../../components/CreatorDashboard/Products/FullProduct/FullProductContent";
import CheckoutContainer from "../../components/FullEventSubComponents/CheckoutContainer";
import { userCurrentNumberOfTickets } from "../../utils/eventUtils/userTicketUtils";
import FullEventInfoTickets from "../../components/FullEvent/FullEventInfoTickets";
import UnlockProductByPhoneNumber from "../../components/CreatorDashboard/Products/FullProduct/UnlockProductByPhoneNumber";

export enum ProductDisplayView {
  LIGHT_THEME = "Light Theme",
  DARK_THEME = "Dark Theme",
}

type FullProductProps = {
  product: Event;
  host: AccountData;
  isAtCapacity: boolean;
  formQuestions: SavedFormQuestion[];
};

const FullProduct = memo(function FullProductFn(props: FullProductProps) {
  const { product, host, isAtCapacity, formQuestions } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hardTheme, setHardTheme] = useState<ThemeType>(product.theme);
  const { theme } = useTheme(hardTheme);
  const { accountData, loggedIn } = useSelector(getAccountState).account;
  const [loading, setLoading] = useState(true);
  const [productIsHidden, setProductIsHidden] = useState(false);
  const [followerData, setFollowerData] = useState<Follower>();
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedCustomTickets, setSelectedCustomTickets] = useState<
    CustomTicketV2[]
  >([]);

  const styles = {
    alternateContainer: {
      margin: isDesktop ? "auto" : undefined,
      paddingTop: isDesktop ? undefined : 160,
      width: isDesktop ? 293 : undefined,
    },
    hiddenProductModal: {
      gap: 10,
      justifyContent: "center",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      padding: 14,
      borderRadius: 20,
      width: isDesktop ? 300 : "80%",
      zIndex: 1,
      boxShadow: "0 12px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.12)",
      height: "fit-content",
    },
  };

  useAsyncOnMount(async () => {
    // Enter if access level is not viewable to all
    if (product.accessLevel !== AccessLevel.ALL) {
      // Always show product if it's a Custom Product type
      if (product.eventType === EventType.CUSTOM_PRODUCT) {
        setLoading(false);
        return;
      }

      if (loggedIn === LoginState.LOGGED_IN) {
        const isMyAccount =
          loggedIn === LoginState.LOGGED_IN &&
          accountData.uid === product.createdBy;
        // Show product if it's the user's own product, unless it's a checkout product that isn't custom product (to demonstrate blurred item)
        if (isMyAccount) {
          const blurCheckout = product.accessLevel === AccessLevel.CHECKOUT;
          setProductIsHidden(blurCheckout);
          setLoading(false);
          return;
        }

        // Check if access level is checkout
        if (product.accessLevel === AccessLevel.CHECKOUT) {
          // Check if logged in user has approved orders to product
          const hasApprovedOrders = await userCurrentNumberOfTickets(
            product.id,
            accountData.uid
          );
          setProductIsHidden(hasApprovedOrders < 1);
          setLoading(false);
        } else {
          // Check if follower is subscribed and within the access level specified
          const followerData = await getUserFollowerData(
            product.createdBy,
            accountData.uid
          );
          if (followerData) {
            setFollowerData(followerData);
            if (product.accessLevel === AccessLevel.SUBSCRIBERS) {
              // Hide product if follower is not subscribed anymore
              setProductIsHidden(
                !checkIfFollowerIsSubscribed(followerData.status)
              );
            } else if (product.accessLevel === AccessLevel.PAID_SUBSCRIBERS) {
              // Hide product if follower is not a paid subscriber
              const isPaidSubscriber =
                followerData.membershipPlanId !== "free" &&
                followerData.membershipState === FollowerMembershipState.ACTIVE;
              setProductIsHidden(!isPaidSubscriber);
            }
          } else {
            setProductIsHidden(true);
          }
        }
      } else {
        // If not logged in, hide product
        setProductIsHidden(true);
      }
    }
    setLoading(false);
  });

  const isSubscribedFollower = useMemo(
    () => followerData && checkIfFollowerIsSubscribed(followerData.status),
    [followerData]
  );

  const isPaymentFailedSubscriber = useMemo(
    () =>
      followerData &&
      followerData.membershipState === FollowerMembershipState.PAYMENT_FAILED,
    [followerData]
  );

  const visibleTickets = useMemo(
    () => getVisibleCustomTickets(product.customTickets, undefined),
    [product.customTickets]
  );

  const toggleDisplayView = useCallback(
    (newDisplayView: ProductDisplayView) => {
      setHardTheme(
        newDisplayView === ProductDisplayView.DARK_THEME
          ? ThemeType.Dark
          : ThemeType.Light
      );
    },
    []
  );

  const navigateProfileOnPress = useCallback(
    (isModal: boolean) => {
      dispatch(DataLoaders.user(host.uid));
      dispatch(accountActions.setRedirectPath(location.pathname));
      let path: "manage" | "memberships" | "subscribe" = "subscribe";
      if (isModal) {
        if (isSubscribedFollower) {
          path = isPaymentFailedSubscriber ? "manage" : "memberships";
        } else if (product.accessLevel === AccessLevel.PAID_SUBSCRIBERS) {
          path = "memberships";
        }
      }
      onProfilePageNavigatePath(navigate, host.username, path);
    },
    [
      dispatch,
      host.uid,
      host.username,
      navigate,
      isSubscribedFollower,
      isPaymentFailedSubscriber,
      product.accessLevel,
    ]
  );

  const renderThemeSwitch = useMemo(
    () => (
      <SlidingToggleButton
        currentValue={
          hardTheme === ThemeType.Dark
            ? ProductDisplayView.DARK_THEME
            : ProductDisplayView.LIGHT_THEME
        }
        onToggle={toggleDisplayView}
        leftOption={{
          value: ProductDisplayView.LIGHT_THEME,
          icon: "ion:sunny",
        }}
        rightOption={{
          value: ProductDisplayView.DARK_THEME,
          icon: "ion:moon",
        }}
        containerStyles={{
          border: `0.5px solid ${
            theme && theme === DarkTheme ? Colors.GRAY8 : Colors.GRAY2
          }`,
        }}
        theme={theme}
      />
    ),
    [hardTheme, toggleDisplayView, theme]
  );

  const renderHiddenModal = useMemo(() => {
    const productName = product.eventType.toLowerCase();
    return productIsHidden ? (
      product.accessLevel === AccessLevel.CHECKOUT ? (
        <div />
      ) : (
        <div
          className="ColumnCentering"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            ...styles.hiddenProductModal,
          }}
        >
          {isSubscribedFollower ? (
            <Icon icon="ion:ribbon" height={35} style={theme.PrimaryText} />
          ) : (
            <ProfilePic user={host} size={35} />
          )}
          <span
            className="bodyMedium"
            style={{
              ...theme.PrimaryText,
              textAlign: "center",
              paddingInline: 20,
            }}
          >
            {followerData && isSubscribedFollower
              ? isPaymentFailedSubscriber
                ? `Update billing info to view this ${productName}`
                : `Upgrade to ${getPossessiveName(
                    host.fullName
                  )} paid membership plan to view this ${productName}`
              : product.accessLevel === AccessLevel.SUBSCRIBERS
                ? `Subscribe to ${host.fullName} to view this ${productName}`
                : `Subscribe to ${getPossessiveName(
                    host.fullName
                  )} paid membership plan to view this ${productName}`}
          </span>
          <RectangleButton
            buttonLabel={
              followerData && isSubscribedFollower
                ? isPaymentFailedSubscriber
                  ? "Manage Billing"
                  : "Upgrade"
                : "Subscribe"
            }
            onPress={() => navigateProfileOnPress(true)}
            altTextColor={
              isPaymentFailedSubscriber ? Colors.WHITE : theme.ButtonText.color
            }
            altColor={
              isPaymentFailedSubscriber
                ? Colors.RED3
                : theme.ButtonBG.backgroundColor
            }
            containerStyles={{ padding: 14 }}
          />
        </div>
      )
    ) : null;
  }, [
    product.eventType,
    product.accessLevel,
    productIsHidden,
    styles.hiddenProductModal,
    isSubscribedFollower,
    theme.PrimaryText,
    theme.ButtonText.color,
    theme.ButtonBG.backgroundColor,
    host,
    followerData,
    isPaymentFailedSubscriber,
    navigateProfileOnPress,
  ]);

  if (loading) {
    return <LoadingScreen isLoadingPage={loading} />;
  }

  return (
    <div
      className="ColumnNormal"
      style={{
        backgroundColor:
          hardTheme === ThemeType.Light ? Colors.WHITE : Colors.BLACK,
        transition: "background-color 0.3s ease",
        minHeight: "100vh",
      }}
    >
      {showCheckout ? (
        <CheckoutContainer
          event={product}
          host={host}
          tixId={""}
          ticketData={undefined}
          ticketToRedeem={undefined}
          promoterId={""}
          promoCode={undefined}
          userReferrer={""}
          selectedCustomTickets={selectedCustomTickets}
          setSelectedCustomTickets={setSelectedCustomTickets}
          eventFormQuestions={formQuestions}
          isEventAtCapacity={isAtCapacity}
          setShowCheckoutForm={setShowCheckout}
          theme={theme}
        />
      ) : (
        <>
          <TopHeader eventTheme={hardTheme} />
          {product.isManualSoldOut ? (
            <div className="ColumnCentering" style={styles.alternateContainer}>
              <InactiveProductState
                eventType={product.eventType}
                host={host}
                theme={theme}
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  position: "relative",
                  paddingBlock: isDesktop ? 50 : 20,
                }}
              >
                {isDesktop && product.accessLevel !== AccessLevel.CHECKOUT ? (
                  <div style={{ position: "absolute", top: 20, left: 20 }}>
                    {renderThemeSwitch}
                  </div>
                ) : null}
                {product.eventType === EventType.POST ? (
                  <FullPostContent
                    post={product}
                    creatorData={host}
                    hardTheme={hardTheme}
                    hiddenModalComp={renderHiddenModal}
                  />
                ) : (
                  <FullProductContent
                    product={product}
                    creatorData={host}
                    hardTheme={hardTheme}
                    hiddenModalComp={renderHiddenModal}
                    aboveBodyComp={
                      productIsHidden &&
                      product.accessLevel === AccessLevel.CHECKOUT &&
                      loggedIn !== LoginState.LOGGED_IN ? (
                        <UnlockProductByPhoneNumber
                          productId={product.id}
                          hardTheme={hardTheme}
                          onUnlocked={() => setProductIsHidden(false)}
                        />
                      ) : undefined
                    }
                    renderCheckoutComp={
                      <FullEventInfoTickets
                        event={product}
                        visibleTickets={visibleTickets}
                        selectedCustomTickets={selectedCustomTickets}
                        setSelectedCustomTickets={setSelectedCustomTickets}
                        promoCode={undefined}
                        redeemTicket={undefined}
                        setShowCheckoutForm={setShowCheckout}
                      />
                    }
                  />
                )}
                {!isDesktop && product.accessLevel !== AccessLevel.CHECKOUT ? (
                  <div
                    className="RowNormal"
                    style={{ paddingTop: 40, paddingInline: 14 }}
                  >
                    {renderThemeSwitch}
                  </div>
                ) : null}
              </div>
              {product.eventType === EventType.POST ? (
                <div
                  className="ColumnCentering"
                  style={{ paddingBlock: 40, gap: 20, ...theme.NewPrimaryBG }}
                >
                  <span
                    style={{
                      fontSize: isDesktop ? 24 : 18,
                      fontWeight: 500,
                      textAlign: "center",
                      ...theme.PrimaryText,
                    }}
                  >
                    Want to hear more from {host.fullName}?
                  </span>
                  <div>
                    <RectangleButton
                      buttonLabel="Subscribe"
                      iconRight={
                        <Icon
                          icon="ion:chevron-forward"
                          height={18}
                          style={{ color: theme.ButtonText.color }}
                        />
                      }
                      onPress={() => navigateProfileOnPress(false)}
                      altTextColor={theme.ButtonText.color}
                      altColor={theme.ButtonBG.backgroundColor}
                      containerStyles={{ padding: 14 }}
                    />
                  </div>
                </div>
              ) : null}
              <Footer eventTheme={hardTheme} creatorData={host} />
            </>
          )}
        </>
      )}
    </div>
  );
});

export default FullProduct;
